import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { getWipReports } from "../reducers/wip.reducer";
import {
  selectWipReports,
  selectLoadingBatch,
} from "../selectors/wip.selector";

/**
 * WIP Batch List Page
 * Displays all available batch update dates
 * Clicking on a batch date navigates to the batch items view
 */
const WIPBatchListPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reports = useSelector(selectWipReports);
  const loading = useSelector(selectLoadingBatch);

  useEffect(() => {
    dispatch(getWipReports());
  }, [dispatch]);

  const handleReportClick = (wipId) => {
    navigate(`/wip/${wipId}`);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f5f7fa",
        pt: 3,
        pb: 6,
      }}
    >
      <Container maxWidth="lg">
        {/* Batch List */}
        <Card
          elevation={2}
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
                flexDirection: "column",
                gap: 2,
              }}
            >
              <CircularProgress />
              <Typography variant="body1" color="text.secondary">
                Loading WIP reports...
              </Typography>
            </Box>
          ) : reports.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No WIP reports available
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {reports.map((report, index) => (
                <ListItem
                  key={report._id}
                  disablePadding
                  sx={{
                    borderBottom:
                      index < reports.length - 1 ? "1px solid #e5e7eb" : "none",
                  }}
                >
                  <ListItemButton
                    onClick={() => handleReportClick(report._id)}
                    sx={{
                      py: 2.5,
                      px: 3,
                      "&:hover": {
                        backgroundColor: "#f5f7fa",
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ fontSize: 24, color: "#4B68D4" }}
                          />
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: "#1a1a1a",
                                mb: 0.5,
                              }}
                            >
                              {report.file?.filename || "Untitled Report"}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 2,
                                flexWrap: "wrap",
                                mt: 0.5,
                              }}
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Vendor:</strong>{" "}
                                {report.supplierName || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Season:</strong>{" "}
                                {report.season || "N/A"}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                <strong>Total Items:</strong>{" "}
                                {report.totalItems || 0}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      }
                      secondary={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 0.5, ml: 5 }}
                        >
                          Click to view and edit WIP items for this report
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Card>
      </Container>
    </Box>
  );
};

export default WIPBatchListPage;
